import { Component, OnInit, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { KTAppCalendarService } from 'src/app/services/event-calendar.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import { PurchaseManagerService } from 'src/app/services/purchase-manager/purchase-manager.service';

declare var ApexCharts: any;

interface CalendarDay {
  date: number;
  isCurrentMonth: boolean;
  isToday: boolean;
}

interface SalesStats {
  totalUnitsSold: number;
  totalSaleAmount: number;
  paymentReceived: number;
  clientOutstanding: number;
}

interface PurchaseStats {
  totalUnitsPurchased: number;
  totalPurchaseValue: number;
  paymentPaid: number;
  vendorOutstanding: number;
}

interface VehicleStats {
  newOnSale: number;
  soldOut: number;
  onOrder: number;
  comingSoon: number;
  reserved: number;
}

interface ClientStats {
  todayRegister: number;
  totalClient: number;
  totalEnquiry: number;
  avgEnquiry: number;
  permittedClients: number;
  blockedClients: number;
}

interface CountryClient {
  country: string;
  count: number;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardComponent extends DestructorComponent implements OnInit, AfterViewInit {

  user: any;
  isClient: boolean = false;

  // BID Statistics
  bidStats = {
    totalBids: 2524,
    avgBids: 97,
    purchased: 99
  };

  // Sales Statistics
  salesStats: SalesStats = {
    totalUnitsSold: 0,
    totalSaleAmount: 0,
    paymentReceived: 0,
    clientOutstanding: 0
  };

  // Purchase Statistics
  purchaseStats: PurchaseStats = {
    totalUnitsPurchased: 0,
    totalPurchaseValue: 0,
    paymentPaid: 0,
    vendorOutstanding: 0
  };

  // Vehicle Statistics
  vehicleStats: VehicleStats = {
    newOnSale: 0,
    soldOut: 0,
    onOrder: 0,
    comingSoon: 0,
    reserved: 0
  };

  // Client Statistics
  clientStats: ClientStats = {
    todayRegister: 0,
    totalClient: 11438,
    totalEnquiry: 15,
    avgEnquiry: 1,
    permittedClients: 11375,
    blockedClients: 63
  };

  dashboardData: any = {};

  // Top 10 Countries
  topCountries: CountryClient[] = [];

  // Event Calendar
  currentDate: Date = new Date();
  currentMonthYear: string = '';
  calendarWeeks: CalendarDay[][] = [];

  private bidChart: any;
  private salesChart: any;
  private purchaseChart: any;
  private vehicleStatsChart: any;
  private enquiryStatsChart: any;
  private topCountriesChart: any;

  constructor(
    private cal: KTAppCalendarService,
    private _globalStateService: GlobalStateService,
    private _generalService: GeneralService
  ) { super() }

  ngAfterViewInit(): void {
    this.cal.init(); // or this.cal.init({ calendarEl: '#my_calendar_id', ... })

    this.initSalesStatisticsChart();
    this.initPurchaseStatisticsChart();
  }

  loadDashboardData(): void {
    this.subscriptions.push(
      this._generalService.DashboardData().subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.dashboardData = response.data;
            this.calculateBidStatistics();
            this.loadVehicleStatistics();
            this.loadClientStatistics();
            this.loadTopCountries();
            this.initEnquiryStatisticsChart(this.dashboardData?.inquiries?.groupByDateObj || {});

          }
        },
        error: (error) => {
          console.error('Error loading dashboard data:', error);
        }
      })
    );
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this._globalStateService.user.subscribe(user => {
        this.user = user;
        // this.isClient = this.user?.role_type === 20;
      })
    );
    this.loadDashboardData();
    this.generateCalendar();
    this.loadSalesStatistics();
    this.loadPurchaseStatistics();
  }

  calculateBidStatistics(): void {
    this.bidStats = {
      totalBids: this.dashboardData?.bids?.allBids || 0,
      avgBids: this.dashboardData?.avg_bids || 0,
      purchased: this.dashboardData?.bids?.purchasedBids || 0
    };

    let groupByDateObj = this.dashboardData?.bids?.groupByDateObj || {};

    let labels: any = [];
    let bids: any = [];
    let purchased: any = [];

    Object.keys(groupByDateObj).forEach((date: any) => {
      labels.push(groupByDateObj[date].day);
      bids.push(groupByDateObj[date].pending + groupByDateObj[date].purchased);
      purchased.push(groupByDateObj[date].purchased || 0);
    });

    this.initBidStatisticsChart({
      labels: labels,
      bids: bids,
      purchased: purchased
    });
  }

  // BID Statistics Chart
  initBidStatisticsChart(data: any): void {
    const element = document.getElementById('kt_bid_statistics_chart');
    if (!element) return;

    const options = {
      series: [
        {
          name: 'Bids',
          data: data.bids
        },
        {
          name: 'Purchased',
          data: data.purchased
        }
      ],
      chart: {
        type: 'bar',
        height: 300,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: data.labels,
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Count',
          style: {
            color: '#A1A5B7',
            fontSize: '12px'
          }
        },
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          }
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val + ' bids';
          }
        }
      },
      colors: ['#5DADE2', '#82E0AA'],
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        labels: {
          colors: '#A1A5B7'
        }
      },
      grid: {
        borderColor: '#EFF2F5',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        }
      }
    };

    this.bidChart = new ApexCharts(element, options);
    this.bidChart.render();
  }

  // Calendar Methods
  generateCalendar(): void {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    // Set current month year display
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    this.currentMonthYear = `${monthNames[month]} ${year}`;

    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Get today's date for comparison
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
    const todayDate = today.getDate();

    // Build calendar weeks
    this.calendarWeeks = [];
    let currentWeek: CalendarDay[] = [];

    // Add previous month's days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      currentWeek.push({
        date: prevMonthLastDay - i,
        isCurrentMonth: false,
        isToday: false
      });
    }

    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push({
        date: day,
        isCurrentMonth: true,
        isToday: isCurrentMonth && day === todayDate
      });

      if (currentWeek.length === 7) {
        this.calendarWeeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add next month's days
    if (currentWeek.length > 0) {
      let nextMonthDay = 1;
      while (currentWeek.length < 7) {
        currentWeek.push({
          date: nextMonthDay++,
          isCurrentMonth: false,
          isToday: false
        });
      }
      this.calendarWeeks.push(currentWeek);
    }
  }

  previousMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.generateCalendar();
  }

  // Load Sales Statistics
  loadSalesStatistics(): void {
    // Mock data - Replace with actual API call when backend is ready
    // Example:
    // const endDate = new Date();
    // const startDate = new Date();
    // startDate.setMonth(startDate.getMonth() - 2);
    // this.invoiceService.GetData('1', '', { start_date: startDate, end_date: endDate }).subscribe(...)

    this.salesStats = {
      totalUnitsSold: 3726,
      totalSaleAmount: 15216296.3,
      paymentReceived: 80679765,
      clientOutstanding: 64494493
    };
  }

  // Load Purchase Statistics
  loadPurchaseStatistics(): void {
    // Mock data - Replace with actual API call when backend is ready
    // Example:
    // const endDate = new Date();
    // const startDate = new Date();
    // startDate.setMonth(startDate.getMonth() - 2);
    // this.purchaseManagerService.GetData('1', '', { start_date: startDate, end_date: endDate }).subscribe(...)

    this.purchaseStats = {
      totalUnitsPurchased: 222,
      totalPurchaseValue: 102096068,
      paymentPaid: 0,
      vendorOutstanding: 102081000
    };
  }

  // Sales Statistics Chart
  initSalesStatisticsChart(): void {
    const element = document.getElementById('kt_sales_statistics_chart');
    if (!element) return;

    const monthLabels = this.getLast2MonthsLabels();
    const daysInMonths = this.getDaysInLast2Months();

    const options = {
      series: [
        {
          name: monthLabels[0], // Previous month (e.g., "Jan")
          data: this.generateSingleMonthData(daysInMonths, 0, 50000, 200000)
        },
        {
          name: monthLabels[1], // Current month (e.g., "Feb")
          data: this.generateSingleMonthData(daysInMonths, 1, 30000, 150000)
        }
      ],
      chart: {
        type: 'area',
        height: 300,
        toolbar: {
          show: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      xaxis: {
        categories: this.generateDayLabelsForMonths(daysInMonths, monthLabels),
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '11px'
          },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          showDuplicates: false,
          trim: true,
          maxHeight: 120
        },
        tickAmount: 15
      },
      yaxis: {
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          },
          formatter: function (val: number) {
            return '¥' + (val / 1000).toFixed(0) + 'k';
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return '¥' + val.toLocaleString();
          }
        }
      },
      colors: ['#7239EA', '#50CD89'],
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        labels: {
          colors: '#A1A5B7'
        }
      },
      grid: {
        borderColor: '#EFF2F5',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      annotations: {
        xaxis: this.getMonthSeparators(daysInMonths, monthLabels)
      }
    };

    this.salesChart = new ApexCharts(element, options);
    this.salesChart.render();
  }

  // Purchase Statistics Chart
  initPurchaseStatisticsChart(): void {
    const element = document.getElementById('kt_purchase_statistics_chart');
    if (!element) return;

    const monthLabels = this.getLast2MonthsLabels();
    const daysInMonths = this.getDaysInLast2Months();

    const options = {
      series: [
        {
          name: monthLabels[0], // Previous month (e.g., "Jan")
          data: this.generateSingleMonthData(daysInMonths, 0, 40000, 180000)
        },
        {
          name: monthLabels[1], // Current month (e.g., "Feb")
          data: this.generateSingleMonthData(daysInMonths, 1, 20000, 120000)
        }
      ],
      chart: {
        type: 'area',
        height: 300,
        toolbar: {
          show: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      xaxis: {
        categories: this.generateDayLabelsForMonths(daysInMonths, monthLabels),
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '11px'
          },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          showDuplicates: false,
          trim: true,
          maxHeight: 120
        },
        tickAmount: 15
      },
      yaxis: {
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          },
          formatter: function (val: number) {
            return '¥' + (val / 1000).toFixed(0) + 'k';
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return '¥' + val.toLocaleString();
          }
        }
      },
      colors: ['#009EF7', '#F1416C'],
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        labels: {
          colors: '#A1A5B7'
        }
      },
      grid: {
        borderColor: '#EFF2F5',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      annotations: {
        xaxis: this.getMonthSeparators(daysInMonths, monthLabels)
      }
    };

    this.purchaseChart = new ApexCharts(element, options);
    this.purchaseChart.render();
  }

  // Get last 2 months labels (e.g., ["Jan", "Feb"])
  getLast2MonthsLabels(): string[] {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date();
    const currentMonth = today.getMonth();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;

    return [monthNames[lastMonth], monthNames[currentMonth]];
  }

  // Get number of days in last 2 months
  getDaysInLast2Months(): number[] {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    // Days in last month
    const daysInLastMonth = new Date(lastMonthYear, lastMonth + 1, 0).getDate();
    // Days in current month
    const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    return [daysInLastMonth, daysInCurrentMonth];
  }

  // Generate day labels for both months (e.g., "1", "2", ..., "31", "1", "2", ..., "28")
  generateDayLabelsForMonths(daysInMonths: number[], monthLabels: string[]): string[] {
    const labels: string[] = [];

    for (let i = 0; i < daysInMonths.length; i++) {
      for (let day = 1; day <= daysInMonths[i]; day++) {
        labels.push(day.toString());
      }
    }

    return labels;
  }

  // Generate monthly data for charts
  generateMonthlyData(daysInMonths: number[], min: number, max: number): number[] {
    const data: number[] = [];
    const totalDays = daysInMonths.reduce((sum, days) => sum + days, 0);

    for (let i = 0; i < totalDays; i++) {
      data.push(Math.floor(Math.random() * (max - min + 1)) + min);
    }

    return data;
  }

  // Generate data for a single month (with null values for the other month)
  // monthIndex: 0 for first month, 1 for second month
  generateSingleMonthData(daysInMonths: number[], monthIndex: number, min: number, max: number): (number | null)[] {
    const data: (number | null)[] = [];

    // For the first month
    if (monthIndex === 0) {
      // Generate data for first month
      for (let i = 0; i < daysInMonths[0]; i++) {
        data.push(Math.floor(Math.random() * (max - min + 1)) + min);
      }
      // Add null values for second month
      for (let i = 0; i < daysInMonths[1]; i++) {
        data.push(null);
      }
    } else {
      // Add null values for first month
      for (let i = 0; i < daysInMonths[0]; i++) {
        data.push(null);
      }
      // Generate data for second month
      for (let i = 0; i < daysInMonths[1]; i++) {
        data.push(Math.floor(Math.random() * (max - min + 1)) + min);
      }
    }

    return data;
  }

  // Get month separators for chart annotations
  getMonthSeparators(daysInMonths: number[], monthLabels: string[]): any[] {
    const separators: any[] = [];
    let position = daysInMonths[0] - 0.5;

    separators.push({
      x: position,
      borderColor: '#999',
      strokeDashArray: 5,
      label: {
        borderColor: '#999',
        style: {
          color: '#fff',
          background: '#999',
          fontSize: '10px'
        },
        text: `${monthLabels[0]}/${monthLabels[1]}`
      }
    });

    return separators;
  }

  // Format currency
  formatCurrency(value: number): string {
    return '¥' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Load Vehicle Statistics
  loadVehicleStatistics(): void {

    this.vehicleStats = {
      newOnSale: this.dashboardData?.vehicles?.notSoldVehicles || 0,
      soldOut: this.dashboardData?.vehicles?.soldVehicles || 0,
      onOrder: this.dashboardData?.vehicles?.on_order || 0,
      comingSoon: this.dashboardData?.vehicles?.coming_soon || 0,
      reserved: this.dashboardData?.vehicles?.reserved || 0
    };

    // Mock data - Replace with actual API call when backend is ready
    // this.vehicleStats = {
    //   newOnSale: 450,
    //   soldOut: 850,
    //   onOrder: 120,
    //   comingSoon: 80,
    //   reserved: 50
    // };
    this.initVehicleStatisticsChart();

  }

  // Load Client Statistics
  loadClientStatistics(): void {

    this.clientStats = {
      todayRegister: this.dashboardData?.clients?.totalClientsToday || 0,
      totalClient: this.dashboardData?.clients?.totalClients || 0,
      totalEnquiry: this.dashboardData?.clients?.totalInquiries || 0,
      avgEnquiry: this.dashboardData?.clients?.avgEnquiry || 0,
      permittedClients: this.dashboardData?.clients?.totalClientsPermitted || 0,
      blockedClients: this.dashboardData?.clients?.totalClientsBlocked || 0
    };

    // Mock data - Replace with actual API call when backend is ready
    // this.clientStats = {
    //   todayRegister: 0,
    //   totalClient: 11438,
    //   totalEnquiry: 15,
    //   avgEnquiry: 1,
    //   permittedClients: 11375,
    //   blockedClients: 63
    // };
  }

  // Load Top Countries
  loadTopCountries(): void {
    // Mock data - Replace with actual API call when backend is ready
    let topCountries = this.dashboardData?.clients?.topCountries || [];
    this.topCountries = topCountries.map((c: any) => {
      return {
        country: c._id,
        count: c.count
      };
    });

    this.initTopCountriesChart();

    // this.topCountries = [
    //   { country: 'Tanzania', count: 900 },
    //   { country: 'Zambia', count: 800 },
    //   { country: 'Uganda', count: 650 },
    //   { country: 'Malawi', count: 600 },
    //   { country: 'Kenya', count: 550 },
    //   { country: 'South Africa', count: 500 },
    //   { country: 'Zimbabwe', count: 450 },
    //   { country: 'Ghana', count: 400 },
    //   { country: 'United Kingdom', count: 350 },
    //   { country: 'Botswana', count: 300 }
    // ];
  }

  // Vehicle Statistics Chart (Donut Chart)
  initVehicleStatisticsChart(): void {
    const element = document.getElementById('kt_vehicle_statistics_chart');
    if (!element) return;

    const options = {
      series: [
        this.vehicleStats.newOnSale,
        this.vehicleStats.soldOut,
        this.vehicleStats.onOrder,
        this.vehicleStats.comingSoon,
        this.vehicleStats.reserved
      ],
      chart: {
        type: 'donut',
        height: 300
      },
      labels: ['New On Sale', 'Sold Out', 'On Order', 'Coming Soon', 'Reserved'],
      colors: ['#50CD89', '#F1416C', '#FFC700', '#009EF7', '#7239EA'],
      legend: {
        position: 'bottom',
        labels: {
          colors: '#A1A5B7'
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                color: '#A1A5B7'
              },
              value: {
                show: true,
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#181C32'
              },
              total: {
                show: true,
                label: 'Total Vehicles',
                fontSize: '14px',
                color: '#A1A5B7',
                formatter: () => {
                  const total = this.vehicleStats.newOnSale + this.vehicleStats.soldOut +
                    this.vehicleStats.onOrder + this.vehicleStats.comingSoon + this.vehicleStats.reserved;
                  return total.toString();
                }
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: false
      }
    };

    this.vehicleStatsChart = new ApexCharts(element, options);
    this.vehicleStatsChart.render();
  }

  // Enquiry Statistics Chart (Line Chart)
  initEnquiryStatisticsChart(data: any): void {
    const element = document.getElementById('kt_enquiry_statistics_chart');
    if (!element) return;

    let labels: any = [];
    let enquiries: any = [];

    Object.keys(data).forEach((date: any) => {
      labels.push(data[date].day);
      enquiries.push(data[date].count);
    });

    // Generate data for 31 days
    const enquiryData = enquiries;

    const options = {
      series: [{
        name: 'Enquiry',
        data: enquiryData
      }],
      chart: {
        type: 'line',
        height: 300,
        toolbar: {
          show: false
        }
      },
      stroke: {
        curve: 'straight',
        width: 2
      },
      xaxis: {
        categories: labels,
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '11px'
          }
        },
        title: {
          text: 'Days',
          style: {
            color: '#A1A5B7',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        max: 3.5,
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          }
        }
      },
      colors: ['#009EF7'],
      markers: {
        size: 4,
        colors: ['#009EF7'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 6
        }
      },
      grid: {
        borderColor: '#EFF2F5',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val + ' enquiries';
          }
        }
      }
    };

    this.enquiryStatsChart = new ApexCharts(element, options);
    this.enquiryStatsChart.render();
  }

  // Top Countries Chart (Bar Chart)
  initTopCountriesChart(): void {
    const element = document.getElementById('kt_top_countries_chart');
    if (!element) return;

    const options = {
      series: [{
        name: 'Clients',
        data: this.topCountries.map(c => c.count)
      }],
      chart: {
        type: 'bar',
        height: 300,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: this.topCountries.map(c => c.country),
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '10px'
          },
          rotate: -45,
          rotateAlways: true
        }
      },
      yaxis: {
        title: {
          text: 'Number of Clients',
          style: {
            color: '#A1A5B7',
            fontSize: '12px'
          }
        },
        labels: {
          style: {
            colors: '#A1A5B7',
            fontSize: '12px'
          }
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val + ' clients';
          }
        }
      },
      colors: ['#F1416C'],
      grid: {
        borderColor: '#EFF2F5',
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        }
      }
    };

    this.topCountriesChart = new ApexCharts(element, options);
    this.topCountriesChart.render();
  }

}
